import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { DocumentDao } from '@app/dao/document/document.dao';
import { UserDao } from '@app/dao/user/user.dao';
import * as _ from 'lodash';
import {
  combineLatest,
  catchError,
  from,
  mergeMap,
  of,
  take,
  tap,
  throwError,
} from 'rxjs';

import { PrModalComponent } from '../pr-modal/pr-modal/pr-modal.component';
import {
  GedDocumentFile,
  GedDocumentFileService,
} from './ged-document-file.service';
import {
  buildGedDocumentTags,
  GedDocumentCheck,
  GedDocumentTag,
  hasNoFile,
  isExpired,
  isUniversignDocument,
} from './ged-document-tags';

const CHECK_CONCURRENCY = 3;
const PAGE_SIZE = 5;

@Component({
  selector: 'ged-picker',
  templateUrl: './ged-picker.component.html',
  styleUrls: ['./ged-picker.component.scss'],
  standalone: false,
})
export class GedPickerComponent implements OnDestroy {
  @Input() accountId: any;

  @Input() documents: string[] = [];

  @Input() ownerId: number;

  @Input() modalTitle: string;

  @Input() modalSubtitle: string;

  @Output() select = new EventEmitter<any>();

  @ViewChild('modalGed', { static: true, read: PrModalComponent })
  modal: PrModalComponent;

  loading = false;

  gedDocuments: any[] = [];

  filteredDocuments: any[] = [];

  displayedDocuments: any[] = [];

  searchValue = '';

  scope: 'owner' | 'all' = 'owner';

  selected: any;

  previewUrl: SafeResourceUrl;

  previewLoading = false;

  previewError: string;

  private owners: any[] = [];

  private documentLabels: { [code: string]: string } = {};

  private documentTypeLabels: { [code: string]: string } = {};

  private checks = new Map<string, GedDocumentCheck>();

  private blobs = new Map<string, SafeResourceUrl>();

  private objectUrls = new Map<string, string>();

  private lastObjectUrl: string;

  private tagCache = new Map<string, GedDocumentTag[]>();

  private visibleCount = PAGE_SIZE;

  constructor(
    private documentDao: DocumentDao,
    private userDao: UserDao,
    private fileService: GedDocumentFileService,
  ) {}

  ngOnDestroy() {
    this.revokeObjectUrls();
  }

  get title(): string {
    return this.modalTitle || 'Choisir un document de la GED';
  }

  get subtitle(): string {
    if (this.modalSubtitle) {
      return this.modalSubtitle;
    }

    const types = (this.documents || [])
      .map(code => this.documentLabels[code] || code)
      .join(', ');

    return types ? `Documents acceptés : ${types}` : '';
  }

  get stepOwnerLabel(): string {
    return this.ownerLabelById(this.ownerId);
  }

  get canFilterByOwner(): boolean {
    return !!this.ownerId && !!this.stepOwnerLabel;
  }

  get countLabel(): string {
    const total = this.filteredDocuments.length;
    const shown = this.displayedDocuments.length;
    const suffix = `document${total > 1 ? 's' : ''}`;

    return shown < total
      ? `${shown} sur ${total} ${suffix}`
      : `${total} ${suffix}`;
  }

  get remainingCount(): number {
    return this.filteredDocuments.length - this.displayedDocuments.length;
  }

  get nextPageSize(): number {
    return Math.min(PAGE_SIZE, this.remainingCount);
  }

  showMore() {
    this.visibleCount += PAGE_SIZE;
    this.applyPagination();
  }

  open() {
    this.searchValue = '';
    this.visibleCount = PAGE_SIZE;
    this.scope = this.ownerId ? 'owner' : 'all';
    this.clearSelection();
    this.modal.open();
    this.load();
  }

  close() {
    this.modal.close();
    this.clearSelection();
    this.revokeObjectUrls();
  }

  onSearchChange(value: string) {
    this.searchValue = value;
    this.visibleCount = PAGE_SIZE;
    this.applyFilters();
  }

  setScope(scope: 'owner' | 'all') {
    this.scope = scope;
    this.visibleCount = PAGE_SIZE;
    this.applyFilters();
  }

  isSelected(doc: any): boolean {
    return !!doc?.id && this.selected?.id === doc.id;
  }

  onRowClick(doc: any) {
    this.selected = doc;

    if (this.hasNoFile(doc)) {
      this.previewUrl = null;
      this.previewLoading = false;
      this.previewError = this.unavailableReason(doc);
      return;
    }

    this.loadPreview(doc);
  }

  canPick(doc: any): boolean {
    return !!doc?.id && !this.isUnavailable(doc) && !!this.previewUrl;
  }

  unavailableAfterTry(doc: any): boolean {
    return !this.hasNoFile(doc) && this.isUnavailable(doc);
  }

  previewButtonTitle(doc: any): string {
    if (this.hasNoFile(doc)) {
      return 'Aucun fichier à afficher';
    }
    return this.unavailableAfterTry(doc) ? "Réessayer l'aperçu" : 'Visualiser';
  }

  pick(doc: any) {
    if (!this.canPick(doc)) {
      return;
    }

    this.close();
    this.select.emit(doc);
  }

  openInTab() {
    if (this.lastObjectUrl) {
      window.open(this.lastObjectUrl, '_blank');
    }
  }

  label(doc: any): string {
    return doc?.label || this.typeLabel(doc) || 'Document sans nom';
  }

  typeLabel(doc: any): string {
    return (
      this.documentTypeLabels[doc?.documentType] ||
      this.documentLabels[doc?.document] ||
      doc?.document ||
      ''
    );
  }

  ownersLabel(doc: any): string {
    const labels = (doc?.ownerIds || [])
      .map(ownerId => this.ownerLabelById(ownerId))
      .filter(Boolean);

    return labels.length ? labels.join(', ') : 'Aucun titulaire';
  }

  hasNoOwner(doc: any): boolean {
    return !(doc?.ownerIds || []).length;
  }

  fileCountLabel(doc: any): string {
    const count = (doc?.files || []).length;
    if (!count) {
      return '';
    }
    return `${count} fichier${count > 1 ? 's' : ''}`;
  }

  formatDate(value: any): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString('fr-FR');
  }

  hasNoFile(doc: any): boolean {
    return hasNoFile(doc);
  }

  isExpired(doc: any): boolean {
    return isExpired(doc);
  }

  isUnavailable(doc: any): boolean {
    return this.hasNoFile(doc) || this.checks.get(doc?.id)?.status === 'failed';
  }

  unavailableReason(doc: any): string {
    if (this.hasNoFile(doc)) {
      return 'Ce document ne contient aucun fichier : il ne peut être ni affiché ni rattaché.';
    }
    if (this.checks.get(doc?.id)?.status === 'failed') {
      return "L'aperçu de ce document n'a pas pu être généré. Réessayez avec l'icône œil ; tant qu'il n'est pas affichable, il ne peut pas être rattaché.";
    }
    return '';
  }

  tags(doc: any): GedDocumentTag[] {
    if (!this.tagCache.has(doc?.id)) {
      this.tagCache.set(
        doc?.id,
        buildGedDocumentTags(doc, this.checks.get(doc?.id)),
      );
    }
    return this.tagCache.get(doc?.id);
  }

  private ownerLabelById(ownerId: any): string {
    return this.owners.find(owner => owner?.id == ownerId)?.label || '';
  }

  private clearSelection() {
    this.selected = null;
    this.previewUrl = null;
    this.previewError = null;
    this.previewLoading = false;
    this.lastObjectUrl = null;
  }

  private loadPreview(doc: any) {
    this.previewUrl = null;
    this.previewError = null;

    if (!doc?.id || !this.accountId) {
      return;
    }

    const cached = this.blobs.get(doc.id);
    if (cached) {
      this.previewUrl = cached;
      this.lastObjectUrl = this.objectUrls.get(doc.id);
      return;
    }

    this.previewLoading = true;

    this.download(doc)
      .pipe(
        catchError(error => {
          if (this.isSelected(doc)) {
            this.previewLoading = false;
            this.previewError = this.unavailableReason(doc);
          }
          return throwError(error);
        }),
      )
      .subscribe(() => {
        if (!this.isSelected(doc)) {
          return;
        }

        this.previewUrl = this.blobs.get(doc.id);
        this.lastObjectUrl = this.objectUrls.get(doc.id);
        this.previewLoading = false;
      });
  }

  private download(doc: any) {
    return this.fileService.download(this.accountId, doc.id).pipe(
      tap(file => this.storeDownload(doc, file)),
      catchError(error => {
        this.setCheck(doc, { status: 'failed' });
        return throwError(error);
      }),
    );
  }

  private storeDownload(doc: any, file: GedDocumentFile) {
    this.objectUrls.set(doc.id, file.objectUrl);
    this.blobs.set(doc.id, file.safeUrl);
    this.setCheck(doc, {
      status: 'ok',
      pageCount: file.pageCount,
      byteLength: file.byteLength,
    });
  }

  private setCheck(doc: any, check: GedDocumentCheck) {
    this.checks.set(doc?.id, check);
    this.tagCache.delete(doc?.id);
  }

  private verifyIdentityDocuments(documents: any[]) {
    const toVerify = documents.filter(
      doc =>
        isUniversignDocument(doc) &&
        !hasNoFile(doc) &&
        !this.checks.has(doc?.id),
    );

    if (!toVerify.length) {
      return;
    }

    toVerify.forEach(doc => this.setCheck(doc, { status: 'pending' }));

    from(toVerify)
      .pipe(
        mergeMap(
          doc => this.download(doc).pipe(catchError(() => of(null))),
          CHECK_CONCURRENCY,
        ),
      )
      .subscribe();
  }

  private applyFilters() {
    const search = (this.searchValue || '').trim().toLowerCase();

    this.filteredDocuments = this.gedDocuments.filter(doc => {
      if (
        this.scope === 'owner' &&
        this.canFilterByOwner &&
        !this.hasNoOwner(doc) &&
        !(doc?.ownerIds || []).some(id => id == this.ownerId)
      ) {
        return false;
      }

      if (!search) {
        return true;
      }

      return `${doc?.label || ''} ${this.ownersLabel(doc)} ${this.typeLabel(
        doc,
      )}`
        .toLowerCase()
        .includes(search);
    });

    this.applyPagination();
  }

  private applyPagination() {
    this.displayedDocuments = this.filteredDocuments.slice(
      0,
      this.visibleCount,
    );
    this.verifyIdentityDocuments(this.displayedDocuments);
  }

  private load() {
    if (!this.accountId) {
      this.gedDocuments = [];
      this.filteredDocuments = [];
      this.displayedDocuments = [];
      return;
    }

    this.loading = true;
    this.gedDocuments = [];
    this.filteredDocuments = [];
    this.displayedDocuments = [];
    this.visibleCount = PAGE_SIZE;
    this.checks.clear();
    this.tagCache.clear();
    this.revokeObjectUrls();

    combineLatest([
      this.documentDao.getReferences(),
      this.userDao.getOwners(),
      this.documentDao.searchDocument(this.accountId, {
        documents: this.documents,
        withSubscriptionFiltering: true,
        visible: true,
      }),
    ])
      .pipe(take(1))
      .subscribe({
        next: ([references, owners, documents]) => {
          this.documentLabels = this.toLabelMap(references?.gedDocuments);
          this.documentTypeLabels = this.toLabelMap(
            references?.gedDocumentTypes,
          );
          this.owners = owners || [];
          this.gedDocuments = this.sortDocuments(
            Array.isArray(documents) ? documents : [],
          );
          this.applyFilters();
          this.loading = false;
        },
        error: error => {
          console.error(error);
          this.loading = false;
        },
      });
  }

  private toLabelMap(references: any[]): { [code: string]: string } {
    return (references || []).reduce(
      (acc, reference) => ({ ...acc, [reference?.code]: reference?.label }),
      {},
    );
  }

  private sortDocuments(documents: any[]): any[] {
    if (!this.ownerId) {
      return documents;
    }

    return _.sortBy(documents, doc => {
      if ((doc?.ownerIds || []).some(id => id == this.ownerId)) {
        return 0;
      }
      return this.hasNoOwner(doc) ? 1 : 2;
    });
  }

  private revokeObjectUrls() {
    this.fileService.revoke(this.objectUrls.values());
    this.objectUrls.clear();
    this.blobs.clear();
  }
}
